const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6h18" stroke="#d32f2f" strokeWidth="2" />
    <rect x="6" y="8" width="12" height="10" rx="2" fill="#d32f2f" fillOpacity="0.15" stroke="#d32f2f" strokeWidth="2" />
    <path d="M9 11v5M12 11v5M15 11v5" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" />
    <rect x="9" y="4" width="6" height="2" rx="1" fill="#d32f2f" />
  </svg>
);
import { useEffect, useState } from 'react';
import { Preferences } from '@capacitor/preferences';
import { SecureStorage } from '../../storage/SecureStorage';
import api from '../../api/Client';
import { restoreAuthFromStorage } from '../../api/Client';
import '../../styles/coupons.css';

type Coupon = {
  code?: string;
  coupon_code?: string;
  discount?: number;
  expiry_date?: string;
};

type StoredUser = {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  sex: string;
  dateOfBirth: string;
  postcode?: number | null;
  postAddress?: string;
  dni?: string | null;
  profilePictureName?: string | null;
};

export default function AddCouponsPage() {
  const [couponCode, setCouponCode] = useState<string>('');
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState<boolean>(true);        // carga inicial de lista
  const [submitting, setSubmitting] = useState<boolean>(false); // estado del envío
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);

  // ---- helpers ----
  const resolveUserId = async (): Promise<number | null> => {
    // 1) SecureStorage (igual que haces en FavoritesPage)
    const storedUser: StoredUser | null = (await (SecureStorage as any)?.getUser?.()) ?? null;
    if (storedUser?.id) return storedUser.id;

    // 2) Preferences('user') – mismo fallback que usas en FavoritesPage
    const userPref = await Preferences.get({ key: 'user' });
    if (userPref.value) {
      try {
        const parsed = JSON.parse(userPref.value) as StoredUser;
        if (parsed?.id) return parsed.id;
      } catch {}
    }

    // 3) Preferences('userId') – último recurso
    const idPref = await Preferences.get({ key: 'userId' });
    if (idPref.value) {
      const asNum = Number(idPref.value);
      if (!Number.isNaN(asNum)) return asNum;
    }

    return null;
  };

  const normalizeList = (body: any): Coupon[] => {
    return Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
  };

  // Carga/recarga lista de cupones (maneja 204 y distintos formatos)
  const loadCoupons = async (uid: number) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/mobile/user/${uid}/coupon`, {
        validateStatus: () => true,
      });

      if (resp.status === 204) {
        setCoupons([]);
      } else if (resp.status >= 200 && resp.status < 300) {
        setCoupons(normalizeList(resp.data));
      } else {
        const msg =
          (typeof resp.data === 'string' && resp.data) ||
          (resp.data && typeof resp.data === 'object' && 'error' in resp.data ? (resp.data as any).error : undefined) ||
          `HTTP ${resp.status}`;
        throw new Error(msg);
      }
    } catch (e: any) {
      console.error('GET coupons error:', e);
      setError('Error al cargar los cupones');
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    (async () => {
      try {
        //restaura tokens a memoria para que el interceptor añada Authorization
        await restoreAuthFromStorage();

        const uid = await resolveUserId();
        if (!uid) {
          setError('No se encontró el usuario. Inicia sesión de nuevo.');
          setLoading(false);
          return;
        }
        setUserId(uid);
        await loadCoupons(uid);
      } catch (e) {
        console.error('init AddCouponsPage error:', e);
        setError('No se pudo iniciar la página de cupones');
        setLoading(false);
      }
    })();
  }, []);

  const addCoupon = async () => {
    setError(null);
    setSuccess(null);

    if (!userId) {
      setError('No se encontró el usuario');
      return;
    }

    const code = couponCode.trim();
    if (!code) {
      setError('Introduce un código de cupón');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await api.post(
        `/api/mobile/user/${userId}/coupon`,
        { coupon_code: code },
        { validateStatus: () => true }
      );


      if (![200, 201, 204].includes(resp.status)) {
        const msg =
          (typeof resp.data === 'string' && resp.data) ||
          (resp.data && typeof resp.data === 'object' && 'error' in resp.data ? (resp.data as any).error : undefined) ||
          `HTTP ${resp.status}`;
        throw new Error(msg);
      }

      setSuccess('Cupón añadido con éxito');
      setCouponCode('');
      await loadCoupons(userId); // refresca lista tras crear
    } catch (err: any) {
      console.error('POST coupon error:', err);
      setError(err?.message || 'Error al añadir el cupón');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="coupons-bg">
      <div className="coupons-content">
        <h2 className="coupons-title">Añadir cupón de descuento</h2>

        <div className="coupons-input-row">
          <span className="coupons-input-icon">
            <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm18 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM7 5.5A2.5 2.5 0 0 1 9.5 3h5A2.5 2.5 0 0 1 17 5.5v13A2.5 2.5 0 0 1 14.5 21h-5A2.5 2.5 0 0 1 7 18.5v-13ZM9.5 5a.5.5 0 0 0-.5.5v13a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 .5-.5v-13a.5.5 0 0 0-.5-.5h-5Z"
                fill="#555"
              />
            </svg>
          </span>
          <input
            type="text"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            placeholder="Código de cupón"
            className="coupons-input"
          />
        </div>

        <button
          onClick={addCoupon}
          className="coupons-btn"
          disabled={submitting || !couponCode.trim()}
        >
          {submitting ? 'Guardando…' : 'Validar y guardar'}
        </button>

        {error && <div className="coupons-error">{error}</div>}
        {success && <div className="coupons-success">{success}</div>}

        <div className="coupons-list-container">
          {loading ? (
            <span>Cargando...</span>
          ) : coupons.length === 0 ? (
            <span>No hay cupones</span>
          ) : (
            <ul className="coupons-list">
              {coupons.map((c, idx) => (
                <li key={idx} className="coupons-list-item coupon-list-flex">
                  <div>
                    <div className="coupons-list-code">Código: {c.code || c.coupon_code}</div>
                    <div>Descuento: {typeof c.discount === 'number' ? `${c.discount}%` : 'N/A'}</div>
                  </div>
                  <button
                    className="coupon-remove-btn"
                    title="Eliminar cupón"
                    onClick={async () => {
                      setError(null);
                      setSuccess(null);
                      if (!userId) return;
                      try {
                        await api.post(`/api/mobile/user/${userId}/coupon/remove`, { coupon_code: c.code || c.coupon_code });
                        setSuccess('Cupón eliminado correctamente');
                        await loadCoupons(userId);
                      } catch (err: any) {
                        setError(err?.message || 'Error al eliminar el cupón');
                      }
                    }}
                  >
                    <TrashIcon />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
